import { KendraConst, SearchInterface, AdminPreferences } from "@amzn/ask-legal-domain";
import { Checkbox, ExpandableSection, SpaceBetween, Box } from "@amzn/awsui-components-react";
import * as React from "react";
import { UIModel } from "../../../model/ui-model";
import { useInstanceDataContext } from "../../../contexts/InstanceDataContext";

interface InstanceFilterProps {
    instanceIds: string[];
    queryInputState: UIModel.State<SearchInterface.SearchQueryInput>;
    disabled?: boolean;
}

const CURRENT_LABEL_STYLE = {
    marginLeft: "5px",
    color: "#6e7a89",
    fontStyle: "italic"
} as const;

const createFilterParams = (instanceId: string) => ({
    attribute: KendraConst.ContainerIngestionKendraAttributes.Default.InstanceId,
    operation: "EqualsTo" as KendraConst.SearchOperation,
    valueMap: {
        value: instanceId,
        valueType: KendraConst.IndexAttributeType.STRING
    }
});

export const InstanceFilter: React.FC<InstanceFilterProps> = ({ instanceIds, queryInputState, disabled }) => {
    const { instanceCache, getCurrentInstanceId, loadInstance } = useInstanceDataContext();
    const currentInstanceId = getCurrentInstanceId();

    // Load instance data for all instanceIds to ensure we have display names
    React.useEffect(() => {
        instanceIds.forEach(instanceId => {
            if (!instanceCache[instanceId]) {
                loadInstance(instanceId).catch(console.error);
            }
        });
    }, [instanceIds, instanceCache, loadInstance]);

    const sortedInstanceIds = React.useMemo(() => {
        const otherInstances = instanceIds.filter(id => id !== currentInstanceId);

        // Sort other instances alphabetically by their display names
        const sortedOtherInstances = otherInstances.sort((a, b) => {
            const nameA = instanceCache[a]?.name || a;
            const nameB = instanceCache[b]?.name || b;
            return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
        });

        return instanceIds.includes(currentInstanceId)
            ? [currentInstanceId, ...sortedOtherInstances]
            : sortedOtherInstances;
    }, [instanceIds, currentInstanceId, instanceCache]);

    // Group instances by organization
    const instancesByOrganization = React.useMemo(() => {
        const awsLegalInstances: string[] = [];
        const amazonLegalInstances: string[] = [];

        sortedInstanceIds.forEach(instanceId => {
            const instance = instanceCache[instanceId];
            if (instance?.preferences?.organization === AdminPreferences.Organization.AWS_LEGAL) {
                awsLegalInstances.push(instanceId);
            } else if (instance?.preferences?.organization === AdminPreferences.Organization.AMAZON_LEGAL) {
                amazonLegalInstances.push(instanceId);
            }
        });

        return { awsLegalInstances, amazonLegalInstances };
    }, [sortedInstanceIds, instanceCache]);

    // Core state calculation: determine which instances are currently selected
    const selectedInstanceIds = React.useMemo(() => {
        const filters = queryInputState.value.filterConfiguration || [];
        const instanceFilters = filters.filter(filter =>
            filter.attribute === KendraConst.ContainerIngestionKendraAttributes.Default.InstanceId
        );

        // If no instance filters exist, all instances are effectively selected
        if (instanceFilters.length === 0) {
            return new Set(sortedInstanceIds);
        }

        // Otherwise, only instances with filters are selected
        const selected = new Set<string>();
        instanceFilters.forEach(filter => {
            if (typeof filter.valueMap.value === "string") {
                selected.add(filter.valueMap.value);
            }
        });

        return selected;
    }, [queryInputState.value.filterConfiguration, sortedInstanceIds]);

    // Calculate checkbox states based on selected instances
    const checkboxStates = React.useMemo(() => {
        const totalInstances = sortedInstanceIds.length;
        const selectedCount = selectedInstanceIds.size;

        // All Teams checkbox state
        const allTeamsChecked = selectedCount === totalInstances;
        const allTeamsIndeterminate = selectedCount > 0 && selectedCount < totalInstances;

        // AWS Legal checkbox state
        const awsLegalSelected = instancesByOrganization.awsLegalInstances.filter(id => selectedInstanceIds.has(id));
        const awsLegalChecked = awsLegalSelected.length === instancesByOrganization.awsLegalInstances.length && instancesByOrganization.awsLegalInstances.length > 0;
        const awsLegalIndeterminate = awsLegalSelected.length > 0 && awsLegalSelected.length < instancesByOrganization.awsLegalInstances.length;

        // Amazon Legal checkbox state
        const amazonLegalSelected = instancesByOrganization.amazonLegalInstances.filter(id => selectedInstanceIds.has(id));
        const amazonLegalChecked = amazonLegalSelected.length === instancesByOrganization.amazonLegalInstances.length && instancesByOrganization.amazonLegalInstances.length > 0;
        const amazonLegalIndeterminate = amazonLegalSelected.length > 0 && amazonLegalSelected.length < instancesByOrganization.amazonLegalInstances.length;

        return {
            allTeams: { checked: allTeamsChecked, indeterminate: allTeamsIndeterminate },
            awsLegal: { checked: awsLegalChecked, indeterminate: awsLegalIndeterminate },
            amazonLegal: { checked: amazonLegalChecked, indeterminate: amazonLegalIndeterminate }
        };
    }, [selectedInstanceIds, sortedInstanceIds, instancesByOrganization]);

    // Helper function to update the query with a new set of selected instances
    const updateSelectedInstances = React.useCallback((newSelectedInstances: Set<string>) => {
        let updatedQuery = { ...queryInputState.value };

        // Remove all existing instance filters
        if (updatedQuery.filterConfiguration) {
            updatedQuery.filterConfiguration = updatedQuery.filterConfiguration.filter(
                filter => filter.attribute !== KendraConst.ContainerIngestionKendraAttributes.Default.InstanceId
            );
        }

        // If all instances are selected, don't add any filters (search all)
        if (newSelectedInstances.size === sortedInstanceIds.length) {
            queryInputState.setValue(updatedQuery);
            return;
        }

        // If no instances are selected, add filter for current instance only
        if (newSelectedInstances.size === 0) {
            updatedQuery = SearchInterface.SearchQueryInput.applyFilter(
                updatedQuery,
                createFilterParams(currentInstanceId)
            );
            queryInputState.setValue(updatedQuery);
            return;
        }

        // Add filters for selected instances
        newSelectedInstances.forEach(instanceId => {
            updatedQuery = SearchInterface.SearchQueryInput.applyFilter(
                updatedQuery,
                createFilterParams(instanceId)
            );
        });

        queryInputState.setValue(updatedQuery);
    }, [queryInputState, sortedInstanceIds, currentInstanceId]);

    // Toggle individual instance
    const toggleInstance = React.useCallback((instanceId: string, checked: boolean) => {
        const newSelected = new Set(selectedInstanceIds);

        if (checked) {
            newSelected.add(instanceId);
        } else {
            newSelected.delete(instanceId);
        }

        updateSelectedInstances(newSelected);
    }, [selectedInstanceIds, updateSelectedInstances]);

    // Toggle all teams
    const toggleAllTeams = React.useCallback((checked: boolean) => {
        if (checked || checkboxStates.allTeams.indeterminate) {
            // Check all teams - select all instances
            updateSelectedInstances(new Set(sortedInstanceIds));
        } else {
            // Uncheck all teams - select only current instance
            updateSelectedInstances(new Set([currentInstanceId]));
        }
    }, [checkboxStates.allTeams.indeterminate, updateSelectedInstances, sortedInstanceIds, currentInstanceId]);

    // Toggle organization instances
    const toggleOrganizationInstances = React.useCallback((organizationInstances: string[], checked: boolean, isIndeterminate: boolean) => {
        const newSelected = new Set(selectedInstanceIds);

        if (checked || isIndeterminate) {
            // Check all instances in this organization
            organizationInstances.forEach(instanceId => {
                newSelected.add(instanceId);
            });
        } else {
            // Uncheck all instances in this organization
            organizationInstances.forEach(instanceId => {
                newSelected.delete(instanceId);
            });
        }

        updateSelectedInstances(newSelected);
    }, [selectedInstanceIds, updateSelectedInstances]);

    const toggleAwsLegal = React.useCallback((checked: boolean) => {
        toggleOrganizationInstances(
            instancesByOrganization.awsLegalInstances,
            checked,
            checkboxStates.awsLegal.indeterminate
        );
    }, [toggleOrganizationInstances, instancesByOrganization.awsLegalInstances, checkboxStates.awsLegal.indeterminate]);

    const toggleAmazonLegal = React.useCallback((checked: boolean) => {
        toggleOrganizationInstances(
            instancesByOrganization.amazonLegalInstances,
            checked,
            checkboxStates.amazonLegal.indeterminate
        );
    }, [toggleOrganizationInstances, instancesByOrganization.amazonLegalInstances, checkboxStates.amazonLegal.indeterminate]);

    const renderInstanceDisplay = React.useCallback((instanceId: string) => {
        const instance = instanceCache[instanceId];
        const displayText = instance?.name || instanceId;
        const isCurrent = instanceId === currentInstanceId;

        return (
            <span>
                {displayText}
                {isCurrent && <span style={CURRENT_LABEL_STYLE}>(Current)</span>}
            </span>
        );
    }, [instanceCache, currentInstanceId]);

    return (
        <ExpandableSection defaultExpanded header="Teams">
            <SpaceBetween direction="vertical" size="s">
                <Checkbox
                    checked={checkboxStates.allTeams.checked}
                    indeterminate={checkboxStates.allTeams.indeterminate}
                    disabled={disabled}
                    onChange={e => toggleAllTeams(e.detail.checked)}
                >
                    All Teams
                </Checkbox>

                {/* Organization-level checkboxes */}
                {instancesByOrganization.awsLegalInstances.length > 0 && (
                    <Checkbox
                        checked={checkboxStates.awsLegal.checked}
                        indeterminate={checkboxStates.awsLegal.indeterminate}
                        disabled={disabled}
                        onChange={e => toggleAwsLegal(e.detail.checked)}
                    >
                        AWS Legal
                    </Checkbox>
                )}

                {instancesByOrganization.amazonLegalInstances.length > 0 && (
                    <Checkbox
                        checked={checkboxStates.amazonLegal.checked}
                        indeterminate={checkboxStates.amazonLegal.indeterminate}
                        disabled={disabled}
                        onChange={e => toggleAmazonLegal(e.detail.checked)}
                    >
                        Amazon Legal
                    </Checkbox>
                )}

                <Box margin={{ top: "xxs", bottom: "xxs" }}>
                    <hr style={{ margin: 0 }} />
                </Box>

                {sortedInstanceIds.map(instanceId => (
                    <Checkbox
                        key={instanceId}
                        checked={selectedInstanceIds.has(instanceId)}
                        disabled={disabled}
                        onChange={e => toggleInstance(instanceId, e.detail.checked)}
                    >
                        {renderInstanceDisplay(instanceId)}
                    </Checkbox>
                ))}
            </SpaceBetween>
        </ExpandableSection>
    );
};
